import './settings.scss';

import React from 'react';
import Slugify from 'slugify';
import FileInput from '../../components/file-input';
import gql from 'nanogql';
import Query from '../../query';

export default class extends React.Component {

  state = {
    saving: false,
    dirty: false,
    logoLoading: false,
    headerImageLoading: false,
    footerImageLoading: false,
    logoError: false,
    headerImageError: false,
    footerImageError: false,
    settings: {
      name: '',
      email: '',
      facebookReviewLink: '',
      googleReviewLink: '',
      headerImage: '',
      footerImage: '',
      logo: '',
      website: '',
      directorInfo: '',
      groups: []
    },
    newGroup: {
      name: '',
      facebookReviewLink: '',
      googleReviewLink: ''
    },
    loading: true
  };

  componentDidMount() {
    this.fetchSettings()
      .catch(console.warn);
  }

  async fetchSettings() {
    const promise = new Promise((resolve) => this.setState({
      loading: true
    }, resolve));

    const query = (this.props.match.params || {}).portal ? (
      gql`query Query($id: ObjectID!){
        getPortalSettings(id: $id) {
            _id,
            email,
            name,
            facebookReviewLink,
            googleReviewLink,
            headerImage,
            footerImage,
            logo,
            website,
            directorInfo,
            groups {
                facebookReviewLink,
                googleReviewLink,
                name,
                disabled
            }
        }      
      }`
    ) : (
        gql`{
            getUserPortalSettings {
                _id,
                email,
                name,
                facebookReviewLink,
                googleReviewLink,
                headerImage,
                footerImage,
                logo,
                website,
                directorInfo,
                groups {
                    facebookReviewLink,
                    googleReviewLink,
                    name,
                    disabled
                }
            }
        }`
    );

    const result = await Query(query, {
      id: (this.props.match.params || {}).portal
    });

    const settings = result.getUserPortalSettings || result.getPortalSettings || this.state.settings;

    await promise;

    return new Promise((resolve) => this.setState({
      loading: false,
      settings
    }, resolve));
  }

  onExistingGroupDisabled(group, disabled) {
    const groups = this.state.settings.groups.slice(),
      index = groups.indexOf(group);

    const object = Object.assign({}, group, {
      disabled: !disabled
    });

    if (index === -1) {
      groups.unshift(object)
    } else {
      groups[index] = object;
    }

    this.setState({
      settings: Object.assign({}, this.state.settings, {
        groups
      }),
      dirty: true
    })
  }

  onExistingGroupInputChange(group, event) {
    const groups = this.state.settings.groups.slice(),
      index = groups.indexOf(group);

    const object = Object.assign({}, group, {
      [event.target.name]: event.target.value
    });

    if (index === -1) {
      groups.push(object)
    } else {
      groups[index] = object;
    }

    this.setState({
      settings: Object.assign({}, this.state.settings, {
        groups
      }),
      dirty: true
    })
  }

  onNewGroupInputChange(event) {
    this.setState({
      newGroup: Object.assign({}, this.state.newGroup, {
        [event.target.name]: event.target.value
      })
    })
  }

  onSettingsInputChange(event) {
    this.setState({
      settings: Object.assign({}, this.state.settings, {
        [event.target.name]: event.target.value
      }),
      dirty: true
    })
  }

  addNewGroup() {
    if (!this.canAddGroup()) {
      return;
    }
    const groups = this.state.settings.groups.slice();
    groups.unshift(this.state.newGroup);
    this.setState({
      settings: Object.assign({}, this.state.settings, {
        groups
      }),
      newGroup: {
        name: '',
        facebookReviewLink: '',
        googleReviewLink: ''
      },
      dirty: true
    })
  }

  canAddGroup() {
    if (!this.state.newGroup.name || typeof this.state.newGroup.name !== 'string') {
      return false;
    }
    const slug = Slugify(this.state.newGroup.name);
    return !this.state.settings.groups.find((group) => (
      this.state.newGroup.name.toLowerCase() === group.name.toLowerCase() || slug.toLowerCase() === Slugify(group.name).toLowerCase()
    ));
  }

  async uploadFile(key, event) {
    if (!(event.target.files && event.target.files.length)) {
      return;
    }
    const promise = new Promise((resolve) => this.setState({
      [`${key}Loading`]: true,
      [`${key}Error`]: false
    }, resolve));

    /**
     * @type {File}
     */
    const file = event.target.files[0];

    const query = gql`        
      mutation Mutation($input: UploadFileInput!, $identity: String) {
          uploadFile(input: $input, identity: $identity) {
              success,
              url
          }
      }
    `;

    let result;

    try {
      result = await Query(query, {
        input: {
          lastModified: file.lastModified || file.lastModifiedDate.getTime(),
          name: file.name,
          type: file.type,
          size: file.size,
          contents: file
        }
      });
    } catch(e) {
      result = {
        uploadFile: {
          success: false,
          url: ''
        }
      };
    }

    await promise;

    return new Promise((resolve) => this.setState({
      [`${key}Loading`]: false,
      [`${key}Error`]: !result.uploadFile.success,
      settings: Object.assign({}, this.state.settings, {
        [key]: result.uploadFile.success ? result.uploadFile.url : this.state.settings[key]
      }),
      dirty: result.uploadFile.success || this.state.dirty
    }, resolve));
  }

  async save() {
    if (!this.state.dirty) {
      return;
    }

    if (!this.state.settings._id) {
      alert('Can not identify review portal to save');
      return;
    }

    const promise = new Promise((resolve) => this.setState({
      saving: true
    }, resolve));

    const query = gql`
      mutation Mutation($input: UpdatePortalSettingsInput!) {
          updatePortalSettings(input: $input)
      }
    `;

    let result;

    try {
      result = await Query(query, {
        input: Object.assign({}, this.state.settings || {}, {
          _id: undefined,
          name: undefined
        })
      });
    } catch(e) {
      result = {
        updatePortalSettings: false
      };
    }

    if (!result.updatePortalSettings) {
      alert('Unable to save settings');
    }

    await promise;

    return new Promise((resolve) => this.setState({
      saving: false,
      dirty: !result.updatePortalSettings
    }, resolve));
  }

  render() {
    return (
      <div className="settings">
        <div className="settings-header">
          <h1><i className="fa fa-cogs"/>&nbsp;&nbsp;Settings</h1>
          <div className="save-wrapper">
            {
              this.state.saving ? (
                <div className="spinner" />
              ) : null
            }
            <button disabled={!this.state.dirty || this.state.saving} onClick={this.save.bind(this)}>Save</button>
          </div>
        </div>
        <div className="settings-company">
          <div className="settings-company-group">
            <label>Company Logo</label>
            <img src={this.state.settings.logo || '/images/placeholder.png'} />
            <FileInput onChange={this.uploadFile.bind(this, 'logo')}>
              <button>Upload Logo</button>
              {
                this.state.logoLoading ? (
                  <div className="spinner" />
                ) : null
              }
            </FileInput>
            {
              this.state.logoError ? (
                <p className="error">
                  Unable to upload file
                </p>
              ) : null
            }
          </div>
          <div className="settings-company-group">
            <label>Header Image</label>
            <img src={this.state.settings.headerImage || '/images/placeholder.png'} />
            <FileInput onChange={this.uploadFile.bind(this, 'headerImage')}>
              <button>Upload Image</button>
              {
                this.state.headerImageLoading ? (
                  <div className="spinner" />
                ) : null
              }
            </FileInput>
            {
              this.state.headerImageError ? (
                <p className="error">
                  Unable to upload file
                </p>
              ) : null
            }
          </div>
          <div className="settings-company-group">
            <label>Footer Image</label>
            <img src={this.state.settings.footerImage || '/images/placeholder.png'} />
            <FileInput onChange={this.uploadFile.bind(this, 'footerImage')}>
              <button>Upload Image</button>
              {
                this.state.footerImageLoading ? (
                  <div className="spinner" />
                ) : null
              }
            </FileInput>
            {
              this.state.footerImageError ? (
                <p className="error">
                  Unable to upload file
                </p>
              ) : null
            }
          </div>
        </div>
        <div className="settings-company">
          <div className="settings-company-group">
            <label>Director Info</label>
            <textarea placeholder="Director Info" rows="3" name="directorInfo" value={this.state.settings.directorInfo} onChange={this.onSettingsInputChange.bind(this)} />
            <label>Email</label>
            <div className="input-wrapper">
              <input type="text" placeholder="Email" name="email" value={this.state.settings.email} onChange={this.onSettingsInputChange.bind(this)} />
              <label><i className="fa fa-envelope" /></label>
            </div>
            <label>Website</label>
            <div className="input-wrapper">
              <input type="text" placeholder="Website" name="website" value={this.state.settings.website} onChange={this.onSettingsInputChange.bind(this)} />
              <label><i className="fa fa-globe" /></label>
            </div>
            <label>Facebook Review Link</label>
            <div className="input-wrapper">
              <input type="text" placeholder="Facebook Review Link" name="facebookReviewLink" value={this.state.settings.facebookReviewLink} onChange={this.onSettingsInputChange.bind(this)} />
              <label><i className="fa fa-facebook" /></label>
            </div>
            <label>Google Review Link</label>
            <div className="input-wrapper">
              <input type="text" placeholder="Google Review Link" name="googleReviewLink" value={this.state.settings.googleReviewLink} onChange={this.onSettingsInputChange.bind(this)} />
              <label><i className="fa fa-google" /></label>
            </div>
          </div>
          <div className="settings-company-group" />
        </div>
        <div className="settings-header">
          <h1>Groups</h1>
        </div>
        <div className="settings-group-new">
          <div className="settings-group-new-input">
            <div className="settings-group-new-input-group">
              <label>Add Group</label>
              <input
                type="text"
                placeholder="Name"
                name="name"
                value={this.state.newGroup.name}
                onChange={this.onNewGroupInputChange.bind(this)}
              />
            </div>
            <div className="settings-group-new-input-group">
              <label>Facebook Review Link</label>
              <div className="input-wrapper">
                <input
                  type="text"
                  placeholder="Facebook Review Link"
                  name="facebookReviewLink"
                  value={this.state.newGroup.facebookReviewLink}
                  onChange={this.onNewGroupInputChange.bind(this)}
                />
                <label><i className="fa fa-facebook" /></label>
              </div>
            </div>
            <div className="settings-group-new-input-group">
              <label>Google Review Link</label>
              <div className="input-wrapper">
                <input
                  type="text"
                  placeholder="Google Review Link"
                  name="googleReviewLink"
                  value={this.state.newGroup.googleReviewLink}
                  onChange={this.onNewGroupInputChange.bind(this)}
                />
                <label><i className="fa fa-google" /></label>
              </div>
            </div>
          </div>
          <button disabled={!this.canAddGroup()} onClick={this.addNewGroup.bind(this)}>Add</button>
        </div>
        {
          this.state.settings.groups
            .map((group, index) => (
              <div className="settings-group" key={index}>
                <div className="settings-group-new-input">
                  <div className="settings-group-new-input-group">
                    <label>Name</label>
                    {group.name}
                  </div>
                  <div className="settings-group-new-input-group">
                    <label>Facebook Review Link</label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        placeholder="Facebook Review Link"
                        name="facebookReviewLink"
                        value={group.facebookReviewLink}
                        onChange={this.onExistingGroupInputChange.bind(this, group)}
                        disabled={group.disabled}
                      />
                      <label><i className="fa fa-facebook" /></label>
                    </div>
                  </div>
                  <div className="settings-group-new-input-group">
                    <label>Google Review Link</label>
                    <div className="input-wrapper">
                      <input
                        type="text"
                        placeholder="Google Review Link"
                        name="googleReviewLink"
                        value={group.googleReviewLink}
                        onChange={this.onExistingGroupInputChange.bind(this, group)}
                        disabled={group.disabled}
                      />
                      <label><i className="fa fa-google" /></label>
                    </div>
                  </div>
                </div>
                <button className={group.disabled ? 'border-only' : ''} onClick={this.onExistingGroupDisabled.bind(this, group, group.disabled)}>
                  {group.disabled ? 'Enable' : 'Disable'}
                </button>
              </div>
            ))
        }
      </div>
    )
  }

}


// WEBPACK FOOTER //
// ./src/pages/settings/index.js